import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, ChevronRight, ChevronLeft } from 'lucide-react';
import DocumentUpload from '../../common/DocumentUpload';
import ConfirmDialog from '../../common/ConfirmDialog';
import { FormService } from '../../../services/patientmr/forms.service';
import { CarePlansService, CarePlanStatus, CarePlanDiscipline } from '../../../services/patientmr/careplans.service';
import type { FormRecordDto, FormDocumentDto } from '../../../dtos/patientmr/forms.dto';
import {
  buildPrefilledFlowsheet,
  extractPdnFrequency,
  isFlowsheetEmpty,
  parseHours,
  PdnFrequency,
} from '../../../utils/flowsheetTotals';
import FlowSheetTotalsBar from './FlowSheetTotalsBar';
import SubmitterCertificationSection from './SubmitterCertificationSection';
import {
  F00120_SERVICE_PROMPT,
  F00120_INITIALS_LABEL,
  F00120_ACK_INSTRUCTION,
  F00120_ACK_INTRO,
  F00120_ACKNOWLEDGEMENTS,
  F00120_COORDINATION_TITLE,
  F00120_COORDINATION_INTRO,
  F00120_COORDINATION,
  F00120_ABBREVIATIONS_INTRO,
  F00120_ABBREVIATIONS,
  type TmhpStatement,
} from '../../../constants/tmhpFormText';
import PrintedNameField from './PrintedNameField';

interface NursingAddendumFormProps {
  isReadOnly?: boolean;
  initialData?: Record<string, any>;
  onDataChange?: (data: any) => void;
  patientId?: string;
  formRecord?: FormRecordDto;
  onFormRecordChange?: (record: FormRecordDto) => void;
}

const CollapsibleSection: React.FC<{
  title: string; sectionKey: string;
  collapsed: boolean; onToggle: () => void;
  children: React.ReactNode; colorClass?: string;
}> = ({ title, collapsed, onToggle, children, colorClass = 'border-slate-200 bg-slate-50' }) => (
  <div className={`rounded-lg border ${colorClass} shadow-sm mb-4`}>
    <div className="flex items-center justify-between cursor-pointer px-4 py-3 hover:bg-slate-100 rounded-t-lg transition-colors" onClick={onToggle}>
      <h4 className="text-base font-semibold text-gray-900 text-left">{title}</h4>
      {collapsed ? <ChevronDown size={16} className="text-gray-500 shrink-0" /> : <ChevronUp size={16} className="text-gray-500 shrink-0" />}
    </div>
    <div className={`px-4 pb-4 pt-2 ${collapsed ? 'hidden' : 'block'}`}>{children}</div>
  </div>
);

const RequiredMark: React.FC = () => <span className="text-red-500 ml-0.5">*</span>;

const inputClass =
  'w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500 text-sm text-left';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1 text-left';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const F00120_CAREGIVER_CODES = ['N', 'O', 'P', 'Q', 'S'];

/** Caregiver <option>s; a stored code outside N/O/P/Q/S (e.g. legacy R) shows as "no longer valid". */
const CaregiverOptions: React.FC<{ value: string }> = ({ value }) => (
  <>
    {value && !F00120_CAREGIVER_CODES.includes(value) && <option value={value}>{value} (no longer valid)</option>}
    <option value=""></option>
    <option value="N">N – PDN</option>
    <option value="O">O – Other</option>
    <option value="P">P – Family</option>
    <option value="Q">Q – PPECC</option>
    <option value="S">S – School</option>
  </>
);

export interface NursingAddendumAck {
  checked: boolean;
  initials: string;
}

/** serviceType from the two F00120 boxes: PDN, PPECC, or BOTH. */
export function serviceTypeFromBoxes(pdn: boolean, ppecc: boolean): '' | 'PDN' | 'PPECC' | 'BOTH' {
  if (pdn && ppecc) return 'BOTH';
  if (pdn) return 'PDN';
  if (ppecc) return 'PPECC';
  return '';
}

/** Section 5 items not yet acknowledged + initialled (mirrors the server-side 422 gate). */
export function missingAcknowledgements(data: Record<string, any>): string[] {
  const acks: Record<string, NursingAddendumAck | undefined> = data.acknowledgements || {};
  const missing: string[] = [];
  if (!['PDN', 'PPECC', 'BOTH'].includes(String(data.serviceType || '').toUpperCase())) missing.push('PDN / PPECC box');
  if (!String(data.clientInitials || '').trim()) missing.push('client/responsible adult page initials');
  const check = (items: TmhpStatement[], label: string) => items.forEach((item, i) => {
    const ack = acks[item.key];
    if (!ack?.checked) missing.push(`${label} ${i + 1}`);
    else if (!String(ack.initials || '').trim()) missing.push(`${label} ${i + 1} (initials)`);
  });
  check(F00120_ACKNOWLEDGEMENTS, 'acknowledgement');
  check(F00120_COORDINATION, 'coordination statement');
  return missing;
}

// Generate 15-min time slots from 00:00 to 23:45
const TIME_SLOTS: string[] = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 15) {
    TIME_SLOTS.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }
}

// Group time slots into 6-hour blocks
const TIME_BLOCKS = [
  { label: '00:00 – 05:45', slots: TIME_SLOTS.slice(0, 24) },
  { label: '06:00 – 11:45', slots: TIME_SLOTS.slice(24, 48) },
  { label: '12:00 – 17:45', slots: TIME_SLOTS.slice(48, 72) },
  { label: '18:00 – 23:45', slots: TIME_SLOTS.slice(72, 96) },
];

interface FlowsheetData {
  [slot: string]: {
    [day: string]: { care: string; caregiver: string };
  };
}

const buildEmptyFlowsheet = (): FlowsheetData => {
  const fs: FlowsheetData = {};
  TIME_SLOTS.forEach(slot => {
    fs[slot] = {};
    DAYS.forEach(day => {
      fs[slot][day] = { care: '', caregiver: '' };
    });
  });
  return fs;
};

const DAY_COLORS: Record<string, string> = {
  Sun: 'bg-purple-50',
  Mon: 'bg-blue-50',
  Tue: 'bg-green-50',
  Wed: 'bg-yellow-50',
  Thu: 'bg-orange-50',
  Fri: 'bg-pink-50',
  Sat: 'bg-indigo-50',
};

const FlowsheetBlock: React.FC<{
  block: { label: string; slots: string[] };
  data: FlowsheetData;
  onChange: (slot: string, day: string, col: 'care' | 'caregiver', value: string) => void;
  isReadOnly: boolean;
  selectedDay: string;
}> = ({ block, data, onChange, isReadOnly, selectedDay }) => {
  const [open, setOpen] = useState(false);
  const [collapsedDays, setCollapsedDays] = useState<Set<string>>(new Set());

  const toggleDay = (day: string) => {
    setCollapsedDays(prev => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day); else next.add(day);
      return next;
    });
  };

  const cellTextClass = collapsedDays.size > 0 ? 'text-sm' : 'text-xs';

  return (
    <div className="border border-slate-200 rounded-md mb-2">
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-2 bg-slate-50 text-sm font-medium text-gray-700 hover:bg-slate-100 rounded-t-md"
        onClick={() => setOpen(o => !o)}
      >
        <span>{block.label}</span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <>
          {/* Mobile: 3-column table for selectedDay only */}
          <div className="block md:hidden overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-slate-200 px-2 py-1 text-left w-16 font-medium">Time</th>
                  <th className="border border-slate-200 px-2 py-1 text-left font-medium">Care</th>
                  <th className="border border-slate-200 px-2 py-1 text-left font-medium w-12">CG</th>
                </tr>
              </thead>
              <tbody>
                {block.slots.map(slot => (
                  <tr key={slot} className="even:bg-gray-50">
                    <td className="border border-slate-200 px-2 py-1 font-mono text-gray-500 w-16 align-top">{slot}</td>
                    <td className="border border-slate-200 px-0.5 py-1 align-top">
                      <textarea
                        rows={3}
                        value={data[slot]?.[selectedDay]?.care ?? ''}
                        onChange={e => onChange(slot, selectedDay, 'care', e.target.value)}
                        readOnly={isReadOnly}
                        className="w-full border-0 bg-transparent px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-slate-400 rounded resize-none overflow-y-auto"
                      />
                    </td>
                    <td className="border border-slate-200 px-0.5 py-1 align-top w-12">
                      <select
                        value={data[slot]?.[selectedDay]?.caregiver ?? ''}
                        onChange={e => onChange(slot, selectedDay, 'caregiver', e.target.value)}
                        disabled={isReadOnly}
                        className="w-full border border-slate-300 bg-white rounded px-1 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                      >
                        <CaregiverOptions value={data[slot]?.[selectedDay]?.caregiver ?? ''} />
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Desktop: full 7-day table with grouped day headers */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-xs border-collapse min-w-[900px]">
              <thead>
                <tr>
                  <th className="border border-slate-200 px-2 py-1 text-left w-16 font-medium bg-gray-100" rowSpan={2}>Time</th>
                  {DAYS.map(day =>
                    collapsedDays.has(day) ? (
                      <th
                        key={`${day}-collapsed`}
                        rowSpan={2}
                        className="border border-slate-200 px-1 py-1 text-center w-8 cursor-pointer select-none bg-gray-100 hover:bg-gray-200"
                        onClick={() => toggleDay(day)}
                      >
                        <div className="flex flex-col items-center gap-0.5">
                          <span className="text-xs font-medium" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>{day}</span>
                          <ChevronRight size={10} className="text-gray-400" />
                        </div>
                      </th>
                    ) : (
                      <th
                        key={day}
                        colSpan={2}
                        className={`border border-slate-200 px-2 py-1 text-center font-semibold text-xs cursor-pointer select-none hover:brightness-95 ${DAY_COLORS[day]}`}
                        onClick={() => toggleDay(day)}
                      >
                        <div className="flex items-center justify-center gap-1">
                          {day}
                          <ChevronLeft size={10} className="text-gray-500" />
                        </div>
                      </th>
                    )
                  )}
                </tr>
                <tr>
                  {DAYS.map(day =>
                    collapsedDays.has(day) ? null : (
                      <React.Fragment key={day}>
                        <th className={`border border-slate-200 px-1 py-1 text-left font-medium min-w-[100px] ${DAY_COLORS[day]}`}>Care</th>
                        <th className={`border border-slate-200 px-1 py-1 text-left font-medium w-12 ${DAY_COLORS[day]}`}>CG</th>
                      </React.Fragment>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {block.slots.map(slot => (
                  <tr key={slot} className="even:bg-gray-50">
                    <td className="border border-slate-200 px-2 py-1 font-mono text-gray-500 w-16 align-top">{slot}</td>
                    {DAYS.map(day =>
                      collapsedDays.has(day) ? (
                        <td key={`${day}-collapsed`} className="border border-slate-200 px-0.5 py-0.5 w-8 text-center text-gray-300 text-xs align-top">·</td>
                      ) : (
                        <React.Fragment key={day}>
                          <td className="border border-slate-200 px-0.5 py-1 min-w-[100px] align-top">
                            <textarea
                              rows={3}
                              value={data[slot]?.[day]?.care ?? ''}
                              onChange={e => onChange(slot, day, 'care', e.target.value)}
                              readOnly={isReadOnly}
                              className={`w-full border-0 bg-transparent px-1 py-0.5 ${cellTextClass} focus:outline-none focus:ring-1 focus:ring-slate-400 rounded resize-none overflow-y-auto`}
                            />
                          </td>
                          <td className="border border-slate-200 px-0.5 py-1 w-12 align-top">
                            <select
                              value={data[slot]?.[day]?.caregiver ?? ''}
                              onChange={e => onChange(slot, day, 'caregiver', e.target.value)}
                              disabled={isReadOnly}
                              className={`w-full border border-slate-300 bg-white rounded px-0.5 py-1 ${cellTextClass} focus:outline-none focus:ring-1 focus:ring-slate-500`}
                            >
                              <CaregiverOptions value={data[slot]?.[day]?.caregiver ?? ''} />
                            </select>
                          </td>
                        </React.Fragment>
                      )
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

/** Section 5 statements: read-only official text, an acknowledgement checkbox and initials. */
const AckStatementList: React.FC<{
  items: TmhpStatement[];
  acks: Record<string, NursingAddendumAck | undefined>;
  onChange: (key: string, patch: Partial<NursingAddendumAck>) => void;
  isReadOnly: boolean;
}> = ({ items, acks, onChange, isReadOnly }) => (
  <ol className="space-y-2 mb-2">
    {items.map((item, idx) => (
      <li key={item.key} className="flex items-start gap-3 text-sm text-gray-700" data-testid={`ack-${item.key}`}>
        <input
          type="checkbox"
          aria-label={`Acknowledge statement ${idx + 1}`}
          checked={acks[item.key]?.checked === true}
          onChange={e => onChange(item.key, { checked: e.target.checked })}
          disabled={isReadOnly}
          className="mt-1 rounded border-gray-300 flex-shrink-0"
        />
        <span className="flex-1 text-left">{item.text}</span>
        <input
          type="text"
          aria-label={`Initials for statement ${idx + 1}`}
          placeholder="Initials"
          maxLength={6}
          value={acks[item.key]?.initials ?? ''}
          onChange={e => onChange(item.key, { initials: e.target.value })}
          readOnly={isReadOnly}
          className="w-20 border border-slate-300 rounded-md px-2 py-1 text-sm flex-shrink-0"
        />
      </li>
    ))}
  </ol>
);

const NursingAddendumForm: React.FC<NursingAddendumFormProps> = ({ isReadOnly = false, initialData = {}, onDataChange, patientId, formRecord, onFormRecordChange }) => {
  const [form, setForm] = useState<Record<string, any>>({
    serviceType: '',
    clientName: '',
    medicaidNumber: '',
    date: '',
    responsibleAdult: '',
    responsibleAdultPhone: '',
    responsibleAdultRelationship: '',
    requestedStartDate: '',
    requestedEndDate: '',
    pdnHoursPerWeek: '',
    ppeccDaysPerWeek: '',
    ppeccHoursPerWeek: '',
    problemList: '',
    goalsOfCare: '',
    measurableOutcomes: '',
    progressTowardGoals: '',
    additionalComments: '',
    healthHistorySummary: '',
    rationaleForHours: '',
    otherInHomeResources: '',
    flowsheet: buildEmptyFlowsheet() as FlowsheetData,
    sigClientAdult: '',
    printedNameClientAdult: '',
    sigDateClientAdult: '',
    sigPDNProvider: '',
    printedNamePDNProvider: '',
    sigDatePDNProvider: '',
    sigPPECCProvider: '',
    printedNamePPECCProvider: '',
    sigDatePPECCProvider: '',
    sigPhysician: '',
    printedNamePhysician: '',
    sigDatePhysician: '',
    clientInitials: '',
    acknowledgements: {} as Record<string, NursingAddendumAck>,
    submitterAgreed: false,
    submitterName: '',
    submitterTitle: '',
    submitterDate: '',
    ...initialData,
  });

  const setAck = (key: string, patch: Partial<NursingAddendumAck>) => {
    if (isReadOnly) return;
    setForm(prev => {
      const acks: Record<string, NursingAddendumAck> = { ...(prev.acknowledgements || {}) };
      acks[key] = { checked: false, initials: '', ...acks[key], ...patch };
      return { ...prev, acknowledgements: acks };
    });
  };

  const svcUpper = String(form.serviceType || '').toUpperCase();
  const pdnBox = svcUpper === 'PDN' || svcUpper === 'BOTH';
  const ppeccBox = svcUpper === 'PPECC' || svcUpper === 'BOTH';
  const ackGaps = missingAcknowledgements(form);

  useEffect(() => {
    onDataChange?.(form);
  }, [form, onDataChange]);

  const set = (field: string, value: any) => {
    if (isReadOnly) return;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const [collapsedSections, setCollapsedSections] = useState<Record<string,boolean>>({});
  const toggleSection = (k: string) => setCollapsedSections(p => ({...p, [k]: !p[k]}));

  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);


  const handleFilesSelected = (files: File[]) => {
    if (formRecord?.status !== 'COMPLETE') {
      setActionError('Document upload is only available when the form is in Complete status.');
      return;
    }
    setActionError(null);
    setStagedFiles(prev => [...prev, ...files]);
  };

  const handleRemoveStagedFile = (index: number) => {
    setStagedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadStagedFiles = async () => {
    if (!stagedFiles.length || !patientId || !formRecord) return;
    setActionError(null);
    setUploading(true);
    try {
      let updatedForm = formRecord;
      for (const file of stagedFiles) {
        updatedForm = await FormService.addDocument(patientId, formRecord.formId, file);
      }
      setStagedFiles([]);
      onFormRecordChange?.(updatedForm);
    } catch (err) {
      console.error('Upload failed:', err);
      setActionError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadDocument = async (doc: FormDocumentDto) => {
    if (!patientId || !formRecord) return;
    setActionError(null);
    try {
      const result = await FormService.getDocumentUrl(patientId, formRecord.formId, doc.id);
      window.open(result.url, '_blank');
    } catch (err) {
      console.error('Download failed:', err);
      setActionError(err instanceof Error ? err.message : 'Download failed. Please try again.');
    }
  };

  const handleDeleteDocument = async (doc: FormDocumentDto) => {
    if (!patientId || !formRecord) return;
    setActionError(null);
    try {
      setDeletingDocId(doc.id);
      const updatedForm = await FormService.deleteDocument(patientId, formRecord.formId, doc.id);
      onFormRecordChange?.(updatedForm);
    } catch (err) {
      console.error('Delete failed:', err);
      setActionError(err instanceof Error ? err.message : 'Delete failed. Please try again.');
    } finally {
      setDeletingDocId(null);
    }
  };

  const handleFlowsheetChange = (slot: string, day: string, col: 'care' | 'caregiver', value: string) => {
    if (isReadOnly) return;
    setForm(prev => {
      const fs = { ...(prev.flowsheet as FlowsheetData) };
      fs[slot] = { ...fs[slot], [day]: { ...fs[slot][day], [col]: value } };
      return { ...prev, flowsheet: fs };
    });
  };

  const [selectedDay, setSelectedDay] = useState<string>('Mon');

  // ── Prefill from the RN care plan (Phase 8 §3.6e) ──────────────────────
  const [prefilling, setPrefilling] = useState(false);
  const [prefillError, setPrefillError] = useState<string | null>(null);
  const [pendingPrefill, setPendingPrefill] = useState<PdnFrequency | null>(null);

  const applyPrefill = (freq: PdnFrequency) => {
    setForm(prev => ({
      ...prev,
      flowsheet: buildPrefilledFlowsheet(freq.hoursPerDay, freq.daysPerWeek, { nightCoverage: freq.nightCoverage }),
    }));
  };

  const handlePrefillFromCarePlan = async () => {
    if (!patientId || isReadOnly) return;
    setPrefillError(null);
    setPrefilling(true);
    try {
      const { carePlans } = await CarePlansService.getCarePlans(patientId);
      const rnPlans = carePlans
        .filter(cp => cp.discipline === CarePlanDiscipline.RN)
        .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      // Active plan: latest COMPLETE, otherwise the latest RN plan
      const plan = rnPlans.find(cp => cp.status === CarePlanStatus.COMPLETE) || rnPlans[0];
      if (!plan) {
        setPrefillError('No RN care plan found for this patient.');
        return;
      }
      const detail = await CarePlansService.getCarePlanDetails(patientId, plan.carePlanId);
      const freq = extractPdnFrequency(detail.data);
      if (!freq) {
        setPrefillError('The RN care plan has no PDN hours per day and days per week to copy.');
        return;
      }
      if (isFlowsheetEmpty(form.flowsheet as FlowsheetData)) {
        applyPrefill(freq);
      } else {
        setPendingPrefill(freq);
      }
    } catch (err) {
      setPrefillError(err instanceof Error && err.message ? err.message : 'Could not load the care plan.');
    } finally {
      setPrefilling(false);
    }
  };

  return (
    <div className="space-y-6 text-left">
      <div className="mb-4">
        <p className="text-xs text-gray-500 uppercase tracking-wide text-left">Form Code: F00120</p>
        <p className="text-sm text-gray-600 text-left">Nursing Addendum to Plan of Care for Private Duty Nursing and/or PPECC</p>
      </div>

      <SubmitterCertificationSection formCode="F00120" data={form} onChange={set} isReadOnly={isReadOnly} />

      <div className="flex flex-wrap items-center gap-3">
        <label className={labelClass} htmlFor="f00120-client-initials">{F00120_INITIALS_LABEL}<RequiredMark /></label>
        <input
          id="f00120-client-initials"
          type="text"
          maxLength={6}
          value={form.clientInitials}
          onChange={e => set('clientInitials', e.target.value)}
          readOnly={isReadOnly}
          className="w-24 border border-slate-300 rounded-md px-2 py-1 text-sm"
        />
        <span className="text-xs text-gray-500">Printed on every page of the F00120 PDF.</span>
      </div>

      {/* Service Type & Client Details */}
      <CollapsibleSection
        title="Service Type & Client Details"
        sectionKey="client-header"
        collapsed={!!collapsedSections['client-header']}
        onToggle={() => toggleSection('client-header')}
        colorClass="border-blue-200 bg-blue-50"
      >
        <p className="text-sm text-gray-700 font-medium mb-1 text-left">{F00120_SERVICE_PROMPT}<RequiredMark /></p>
        <div className="flex gap-6">
          <label className="flex items-center gap-2 text-sm text-gray-700 font-medium">
            <input type="checkbox" checked={pdnBox} onChange={e => set('serviceType', serviceTypeFromBoxes(e.target.checked, ppeccBox))} disabled={isReadOnly} />
            PDN
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 font-medium">
            <input type="checkbox" checked={ppeccBox} onChange={e => set('serviceType', serviceTypeFromBoxes(pdnBox, e.target.checked))} disabled={isReadOnly} />
            PPECC
          </label>
        </div>
        {!pdnBox && !ppeccBox && <p className="mt-1 text-xs text-red-500 text-left">Check PDN, PPECC, or both</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Client Name<RequiredMark /></label>
            <input type="text" value={form.clientName} onChange={e => set('clientName', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Medicaid Number<RequiredMark /></label>
            <input type="text" value={form.medicaidNumber} onChange={e => set('medicaidNumber', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Date</label>
            <input type="date" value={form.date} onChange={e => set('date', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Name of Responsible Adult</label>
            <input type="text" value={form.responsibleAdult} onChange={e => set('responsibleAdult', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Responsible Adult Telephone</label>
            <input type="tel" value={form.responsibleAdultPhone} onChange={e => set('responsibleAdultPhone', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Relationship to Client</label>
            <input type="text" value={form.responsibleAdultRelationship} onChange={e => set('responsibleAdultRelationship', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Requested Start Date<RequiredMark /></label>
            <input type="date" value={form.requestedStartDate} onChange={e => set('requestedStartDate', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Requested End Date<RequiredMark /></label>
            <input type="date" value={form.requestedEndDate} onChange={e => set('requestedEndDate', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Number of PDN Hours Requested per Week</label>
            <input type="number" min={0} value={form.pdnHoursPerWeek} onChange={e => set('pdnHoursPerWeek', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Number of PPECC Days Requested per Week</label>
            <input type="number" min={0} value={form.ppeccDaysPerWeek} onChange={e => set('ppeccDaysPerWeek', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Number of PPECC Hours Requested per Week</label>
            <input type="number" min={0} value={form.ppeccHoursPerWeek} onChange={e => set('ppeccHoursPerWeek', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
        </div>
      </CollapsibleSection>

      {/* Section 1 */}
      <CollapsibleSection
        title="Section 1: Nursing Care Plan Summary"
        sectionKey="nursing-care-plan"
        collapsed={!!collapsedSections['nursing-care-plan']}
        onToggle={() => toggleSection('nursing-care-plan')}
        colorClass="border-indigo-200 bg-indigo-50"
      >
        <div className="space-y-4">
          {[
            { field: 'problemList', label: 'Problem List' },
            { field: 'goalsOfCare', label: 'Goals of Care' },
            { field: 'measurableOutcomes', label: 'Specific Measurable Outcomes' },
            { field: 'progressTowardGoals', label: 'Progress Toward Goals' },
            { field: 'additionalComments', label: 'Additional Comments' },
          ].map(({ field, label }) => (
            <div key={field}>
              <label className={labelClass}>{label}</label>
              <textarea rows={3} value={form[field]} onChange={e => set(field, e.target.value)} readOnly={isReadOnly} className={inputClass} />
            </div>
          ))}
        </div>
      </CollapsibleSection>

      {/* Section 2 */}
      <CollapsibleSection
        title="Section 2: Summary of Recent Health History"
        sectionKey="health-history"
        collapsed={!!collapsedSections['health-history']}
        onToggle={() => toggleSection('health-history')}
        colorClass="border-violet-200 bg-violet-50"
      >
        <p className="text-xs text-gray-500 mb-2 text-left">
          For initial authorization or 90-day summary for extension of PDN and/or PPECC services. Include recent hospitalizations, emergency room visits, surgery, and other significant health events.
        </p>
        <label className={labelClass}>Summary<RequiredMark /></label>
        <textarea rows={6} value={form.healthHistorySummary} onChange={e => set('healthHistorySummary', e.target.value)} readOnly={isReadOnly} className={inputClass} />
      </CollapsibleSection>

      {/* Section 3 */}
      <CollapsibleSection
        title="Section 3: Rationale for PDN/PPECC Hours"
        sectionKey="rationale"
        collapsed={!!collapsedSections['rationale']}
        onToggle={() => toggleSection('rationale')}
        colorClass="border-amber-200 bg-amber-50"
      >
        <p className="text-xs text-gray-500 mb-2 text-left">
          For initial requests, as well as requests to increase, decrease, or stay the same.
        </p>
        <label className={labelClass}>Rationale<RequiredMark /></label>
        <textarea rows={6} value={form.rationaleForHours} onChange={e => set('rationaleForHours', e.target.value)} readOnly={isReadOnly} className={inputClass} />
      </CollapsibleSection>

      {/* Section 4 — Daily Flow Sheet */}
      <CollapsibleSection
        title="Section 4: Schedule of Services (24-Hour Flow Sheet)"
        sectionKey="flow-sheet"
        collapsed={!!collapsedSections['flow-sheet']}
        onToggle={() => toggleSection('flow-sheet')}
        colorClass="border-green-200 bg-green-50"
      >
        <div className="mb-3">
          <label className={labelClass}>Other in-home resources</label>
          <input type="text" value={form.otherInHomeResources} onChange={e => set('otherInHomeResources', e.target.value)} readOnly={isReadOnly} className={inputClass} />
        </div>
        <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded text-xs text-gray-600">
          <strong>Care Giver (CG) Codes:</strong> N = PDN hours, O = other in-home resource(s), specify name above, P = family (if family has volunteered), Q = PPECC hours, S = school/daycare
        </div>
        {/* Official F00120 page-2 abbreviation list (read-only, verbatim) */}
        <details className="mb-3 rounded border border-slate-200 bg-white text-xs text-gray-700" data-testid="f00120-abbreviations">
          <summary className="cursor-pointer px-3 py-2 font-semibold text-gray-800">Service abbreviations (F00120 page 2)</summary>
          <div className="px-3 pb-3">
            <p className="mb-2">{F00120_ABBREVIATIONS_INTRO}</p>
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="py-1 pr-3 font-semibold">Abbreviation</th>
                  <th className="py-1 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                {F00120_ABBREVIATIONS.map(row => (
                  <tr key={row.abbreviation} className="border-b border-slate-100">
                    <td className="py-0.5 pr-3 font-medium whitespace-nowrap">{row.abbreviation}</td>
                    <td className="py-0.5">{row.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
        {/* Mobile day-selector tabs — hidden on md+ */}
        <div className="grid grid-cols-7 gap-1 mb-3 md:hidden">
          {DAYS.map(day => (
            <button
              key={day}
              type="button"
              onClick={() => setSelectedDay(day)}
              className={`py-3 rounded-lg text-sm font-semibold transition-colors text-center ${
                selectedDay === day
                  ? 'bg-slate-800 text-white shadow-sm'
                  : 'bg-slate-100 text-gray-600 hover:bg-slate-200 active:bg-slate-300'
              }`}
            >
              {day}
            </button>
          ))}
        </div>
        {!isReadOnly && patientId && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handlePrefillFromCarePlan}
              disabled={prefilling}
              className="px-3 py-1.5 text-sm font-medium rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {prefilling ? 'Loading care plan...' : 'Prefill from care plan'}
            </button>
            <span className="text-xs text-gray-500">Fills code N for the RN care plan's hours per day and days per week. Cells stay editable.</span>
          </div>
        )}
        {prefillError && <p className="mb-3 text-xs text-red-600" role="alert">{prefillError}</p>}
        <FlowSheetTotalsBar
          className="mb-3"
          grid={form.flowsheet as FlowsheetData}
          targets={[{ label: 'PDN hours requested per week on this form', hours: parseHours(form.pdnHoursPerWeek) }]}
        />
        {TIME_BLOCKS.map(block => (
          <FlowsheetBlock
            key={block.label}
            block={block}
            data={form.flowsheet as FlowsheetData}
            onChange={handleFlowsheetChange}
            isReadOnly={isReadOnly}
            selectedDay={selectedDay}
          />
        ))}
        <ConfirmDialog
          open={pendingPrefill !== null}
          title="Replace the flow sheet?"
          message="The flow sheet already has entries. Prefilling from the care plan clears every cell and fills code N for the care plan's hours."
          confirmLabel="Replace"
          destructive
          onConfirm={() => {
            if (pendingPrefill) applyPrefill(pendingPrefill);
            setPendingPrefill(null);
          }}
          onCancel={() => setPendingPrefill(null)}
        />
      </CollapsibleSection>

      {/* Section 5 — Acknowledgements */}
      <CollapsibleSection
        title="Section 5: Acknowledgements"
        sectionKey="acknowledgements"
        collapsed={!!collapsedSections['acknowledgements']}
        onToggle={() => toggleSection('acknowledgements')}
        colorClass="border-red-200 bg-red-50"
      >
        <p className="text-sm font-semibold text-gray-800 mb-2 text-left">{F00120_ACK_INSTRUCTION}</p>
        <p className="text-sm text-gray-700 mb-2 text-left">{F00120_ACK_INTRO}</p>
        <AckStatementList items={F00120_ACKNOWLEDGEMENTS} acks={form.acknowledgements || {}} onChange={setAck} isReadOnly={isReadOnly} />

        <h5 className="text-sm font-semibold text-gray-900 mt-4 mb-1 text-left">{F00120_COORDINATION_TITLE}</h5>
        <p className="text-sm text-gray-700 mb-2 text-left">{F00120_COORDINATION_INTRO}</p>
        <AckStatementList items={F00120_COORDINATION} acks={form.acknowledgements || {}} onChange={setAck} isReadOnly={isReadOnly} />

        {ackGaps.length > 0 && !isReadOnly && (
          <div className="my-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800" role="status">
            Required before this form can be submitted or finalized: {ackGaps.join('; ')}.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <PrintedNameField signer="Client/Responsible Adult" value={form.sigClientAdult} onChange={v => set('sigClientAdult', v)} readOnly={isReadOnly} inputClassName={inputClass} labelClassName={labelClass} />
          </div>
          <div>
            <label className={labelClass}>Printed Name</label>
            <input type="text" value={form.printedNameClientAdult} onChange={e => set('printedNameClientAdult', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Date</label>
            <input type="date" value={form.sigDateClientAdult} onChange={e => set('sigDateClientAdult', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <PrintedNameField signer="PDN Provider" value={form.sigPDNProvider} onChange={v => set('sigPDNProvider', v)} readOnly={isReadOnly} inputClassName={inputClass} labelClassName={labelClass} />
          </div>
          <div>
            <label className={labelClass}>Printed Name<RequiredMark /></label>
            <input type="text" value={form.printedNamePDNProvider} onChange={e => set('printedNamePDNProvider', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Date</label>
            <input type="date" value={form.sigDatePDNProvider} onChange={e => set('sigDatePDNProvider', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <PrintedNameField signer="PPECC Provider" value={form.sigPPECCProvider} onChange={v => set('sigPPECCProvider', v)} readOnly={isReadOnly} inputClassName={inputClass} labelClassName={labelClass} />
          </div>
          <div>
            <label className={labelClass}>Printed Name<RequiredMark /></label>
            <input type="text" value={form.printedNamePPECCProvider} onChange={e => set('printedNamePPECCProvider', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Date</label>
            <input type="date" value={form.sigDatePPECCProvider} onChange={e => set('sigDatePPECCProvider', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <PrintedNameField signer="Prescribing Physician" value={form.sigPhysician} onChange={v => set('sigPhysician', v)} readOnly={isReadOnly} inputClassName={inputClass} labelClassName={labelClass} />
          </div>
          <div>
            <label className={labelClass}>Printed Name<RequiredMark /></label>
            <input type="text" value={form.printedNamePhysician} onChange={e => set('printedNamePhysician', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Date</label>
            <input type="date" value={form.sigDatePhysician} onChange={e => set('sigDatePhysician', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
        </div>
      </CollapsibleSection>

      {/* Generate PDF moved to page action bar */}

      {actionError && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex justify-between items-start">
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)} className="ml-2 text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      {/* Documents */}
      {formRecord && (
        <>
          <DocumentUpload
            documents={(formRecord.documents ?? []).map(d => ({
              id: d.id,
              name: d.name,
              size: d.size,
              uploadedAt: d.uploadedAt,
            }))}
            stagedFiles={stagedFiles}
            canUpload={formRecord.status === 'COMPLETE'}
            onFilesSelected={handleFilesSelected}
            onStagedRemove={handleRemoveStagedFile}
            onDownload={handleDownloadDocument}
            onDelete={handleDeleteDocument}
            uploading={uploading}
            deletingId={deletingDocId}
            collapsible={true}
            defaultOpen={true}
            maxSizeMb={7}
          />
          {stagedFiles.length > 0 && formRecord.status === 'COMPLETE' && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleUploadStagedFiles}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? 'Uploading...' : 'Upload Files'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NursingAddendumForm;
